import React, { createContext, useContext, useState, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { toast } from '@/components/ui/use-toast';
import { supabase } from '@/lib/supabase';

interface Video {
  id: string;
  name: string;
  url: string;
  created_at: string;
}

interface AppContextType {
  sidebarOpen: boolean;
  toggleSidebar: () => void;
  heroVideo: string | null;
  setHeroVideo: (url: string | null) => void;
  videos: Video[];
  loadVideos: () => Promise<void>;
}

const defaultAppContext: AppContextType = {
  sidebarOpen: false,
  toggleSidebar: () => {},
  heroVideo: null,
  setHeroVideo: () => {},
  videos: [],
  loadVideos: async () => {},
};

const AppContext = createContext<AppContextType>(defaultAppContext);

export const useAppContext = () => useContext(AppContext);

export const AppProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [heroVideo, setHeroVideoState] = useState<string | null>(null);
  const [videos, setVideos] = useState<Video[]>([]);

  const toggleSidebar = () => {
    setSidebarOpen(prev => !prev);
  };

  const setHeroVideo = (url: string | null) => {
    setHeroVideoState(url);
  };

  const loadVideos = async () => {
    try {
      const { data, error } = await supabase.storage
        .from('videos')
        .list('', {
          limit: 100,
          offset: 0,
          sortBy: { column: 'created_at', order: 'desc' }
        });

      if (error) throw error;

      const videoList = data?.map(file => {
        const { data: urlData } = supabase.storage
          .from('videos')
          .getPublicUrl(file.name);
        
        return {
          id: file.id || file.name,
          name: file.name,
          url: urlData.publicUrl,
          created_at: file.created_at || new Date().toISOString()
        };
      }) || [];

      setVideos(videoList);
      
      // Set the most recent video as hero video if none is set
      if (!heroVideo && videoList.length > 0) {
        setHeroVideoState(videoList[0].url);
      }
    } catch (error) {
      console.error('Error loading videos:', error);
    }
  };

  useEffect(() => {
    loadVideos();
  }, []);

  return (
    <AppContext.Provider
      value={{
        sidebarOpen,
        toggleSidebar,
        heroVideo,
        setHeroVideo,
        videos,
        loadVideos,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};